/* eslint-disable */
import { getAllEntityTypes } from '@/components/map-v2/data/entity-configs.js'
import { MurphyService } from '@/util/murphy.service'
import { normalizeMurphyResult } from '@/components/map-v2/data/murphy-helpers'
import {
  extractVehiculoFromMurphyResult,
  extractLprDataFromMurphyResult,
} from '@/components/map-v2/data/murphy-vehiculo-extractor'
import radioTranscriptsDb from '@/components/map-v2/data/radio-transcripts-db'
import { purgeReportantState, stripKpiIncidents } from '@/util/user-permissions'
import { purgarEstadoDeCapas, capaPermitida } from '@/components/map-v2/data/layer-permissions'
import {
  reconcileTiles,
  migrateTrayGrid,
  buildPresetTiles,
  resolvePresetToTray,
} from '@/components/map-v2/utils/mosaic-packer'
import TrayPresetsService from '@/util/tray-presets.service'
import { syncPresets, applyPendingOps } from '@/components/map-v2/data/tray-presets-sync'
import { clearCache as clearEntityCacheDb } from '@/components/map-v2/utils/entity-cache'
// Mock TETRA — import estático para que el panel siempre tenga canales
// disponibles desde el primer render (sin dynamic import asíncrono).
// tetra-mock.js eliminado tras el merge de edomex-test — el catálogo
// de canales viene del store radioGroups (WS upsert con datos reales).

const LS_PREFIX = 'kalan:map-v2:'

function ls(key, fallback) {
  try {
    const raw = localStorage.getItem(LS_PREFIX + key)
    if (raw === null) return fallback
    return JSON.parse(raw)
  } catch {
    return fallback
  }
}

function lsSet(key, value) {
  try {
    localStorage.setItem(LS_PREFIX + key, JSON.stringify(value))
  } catch {
    // ignore quota errors
  }
}

function buildDefaultLayerVisibility() {
  const vis = {}
  getAllEntityTypes().forEach(t => { vis[t] = true })
  return vis
}

const state = {
  viewport: ls('viewport', {
    center: null,
    zoom: null,
    styleId: 'dark',
  }),
  panels: ls('panels', {
    sidebar: true,
    drawer: false,
    tray: false,
    murphyBuscador: false,
    murphyDrawer: false,
    sosLeft: false,
    smartSearch: false,
    newIncident: false,
    incidentDispatch: false,
    radioTetra: false,
    incidentTicker: false,
  }),
  // Seed pre-poblado para abrir el panel "Crear incidente" desde el menú
  // contextual del mapa. NO persiste — se setea al abrir y se limpia al
  // cerrar. Shape: { latitude, longitude, idTipoDispositivo? }.
  newIncidentSeed: null,
  // ID del incidente activo en el panel de Despacho. NO persiste — se setea
  // al abrir y se limpia al cerrar el panel. La data completa del incidente
  // se lee desde selectedItem para evitar duplicar payloads pesados.
  dispatchTargetIncidentId: null,

  // ───────── Radio TETRA (POC sin backend) ─────────
  // Target principal del panel TETRA. Shape:
  //   device → { type:'device', monitorId, uniqueId, name, groupName }
  //   channel → { type:'channel' } (no necesita más datos; el canal se elige en radioActiveChannelIds)
  //   null   → panel abierto sin target (modo canal libre por default)
  // NO persiste — comunicación radial es estrictamente de sesión.
  radioTetraTarget: null,
  // Modo activo del panel: 'device' | 'channel'. NO persiste.
  radioTetraMode: 'channel',
  // Devices TETRA adicionales sumados al "canal de comunicación" via el
  // diálogo cambiar/agregar. Aplica solo a UX local — el modelo MCX no
  // soporta multi-device por llamada, pero mantenemos chips informativos
  // para el operador. NO persiste.
  // Shape: [{ monitorId, uniqueId, name, groupName }, ...]
  radioTetraParticipants: [],
  // Historial efímero de actividad del canal (TX/RX). NO persiste.
  // Limitado a 50 entradas FIFO desde el componente. Shape por entrada:
  //   { id, direction:'TX'|'RX', channelId, channelName, callerId, callerName,
  //     durationMs, timestamp }
  radioActivityLog: [],

  // Bus simple para que cualquier modal/panel pueda pedir al mapa que el
  // operador seleccione una coordenada con un clic. El picker es de un solo
  // disparo: el siguiente clic en el mapa lo resuelve y se desactiva.
  // Shape: { active, prompt, result: {lat, lng} | null, requestId }
  locationPicker: { active: false, prompt: '', result: null, requestId: 0 },
  layerVisibility: ls('layerVisibility', buildDefaultLayerVisibility()),
  // Filtros por entidad — controlan qué features se incluyen en el source
  // GeoJSON via rebuild (sin filter expressions en los layers para evitar
  // overhead de 114K evaluaciones/frame con 19K features).
  entityFilters: ls('entityFilters', {
    incident: { status: ['alert', 'idle'], category: ['proteccion_civil', 'servicios_publicos', 'medico', 'seguridad', 'asistencia', 'mal_uso', 'otros_servicios'] },
    // ownership = ámbito (estatal C5 / municipal / otras). Los 3 activos
    // por default; usuarios con entityFilters ya persistido no tienen la
    // key y el toolbar trata su ausencia como "todos visibles".
    camera:   { status: ['online'], ownership: ['estatal', 'municipal', 'otro'] },
    bus:      { status: ['online', 'alert'] },
    // LPR 'partial' incluido por default: es degradado pero operativo;
    // ocultarlo deja un gap operativo confuso para el operador.
    lpr:      { status: ['online', 'partial'] },
    // Device 'unknown' y 'down' incluidos por default: 'down' es equipo
    // fallando (crítico) y 'unknown' es sin telemetría reciente (útil).
    device:   { status: ['online', 'unknown', 'down'] },
    // Sensor 'reported' incluido por default: alerta atendida pendiente
    // de cierre — el operador debe verla hasta que se cierre.
    sensor:   { status: ['online', 'alert', 'reported'] },
  }),
  // 4 filtros independientes para geocercas: las "generales" (sin mandoUnico)
  // y los 3 niveles del mando único (región, sector, cuadrante).
  //
  // Default = TODOS APAGADOS. Antes arrancábamos con todos prendidos, lo que
  // dejaba el catálogo global cargado en pantalla aunque el operador nunca
  // lo hubiera pedido. Combinado con el cache IDB de geocercas
  // (entity-cache.js), las geocercas se quedaban "pegadas" sesión tras
  // sesión. El operador tiene que activarlas explícitamente desde el
  // toolbar si las quiere ver.
  //
  // Versioning: forzamos un reset único para usuarios existentes que ya
  // tenían `geofenceFilters` con `all true` persistido. Sin esto, el
  // cambio de default no afectaría a quien ya migró antes.
  geofenceFilters: (function() {
    const defaults = { geofence: false, region: false, sector: false, cuadrante: false }
    const VERSION_KEY = 'geofenceFiltersVersion'
    const CURRENT_VERSION = 2
    const storedVersion = ls(VERSION_KEY, 1)
    if (storedVersion < CURRENT_VERSION) {
      lsSet(VERSION_KEY, CURRENT_VERSION)
      lsSet('geofenceFilters', defaults)
      return defaults
    }
    const persisted = ls('geofenceFilters', null)
    if (persisted && typeof persisted === 'object') {
      return Object.assign({}, defaults, persisted)
    }
    return defaults
  })(),
  heatmapActive: ls('heatmapActive', false),
  // Remarcado visual del EdoMex (mask externo + halo + línea). On por default.
  jurisdictionOutlineVisible: ls('jurisdictionOutlineVisible', true),
  // Atenuación al 25% de labels del style fuera del EdoMex. On por default.
  externalLabelsAttenuated: ls('externalLabelsAttenuated', true),
  // Modo de visualizacion de etiquetas de markers (icon labels):
  //   'dense'   — todas las etiquetas siempre visibles (sin collision detection).
  //   'smart'   — collision detection con padding pequeno y prioridad por
  //               tipo (incident/sos_call/sensor:alert/reportant siempre on).
  //   'minimal' — comportamiento legacy: solo entidades dinamicas con
  //               collision detection default; estaticas sin label.
  // Las "criticas" (labelPriority>=3) siempre se ven en los 3 modos.
  labelMode: ls('labelMode', 'smart'),
  clusterEnabled: ls('clusterEnabled', true),
  clusterMaxZoom: ls('clusterMaxZoom', 13),
  // Filtro transitorio desde cards del drawer KPI. Cuando es no-null,
  // el sidebar/mapa aplican un filtro adicional por data.category del
  // incidente. No se persiste: es interacción puntual del drawer, no
  // estado de sesión.
  kpiCategoryFilter: null,
  // Anchos fijos de los paneles. El usuario pidio eliminar el resize,
  // asi que estos valores son constantes y no se modifican.
  panelSizes: {
    sidebar: 280,
    drawer: 340,
    // Altura del tray docked. Se usa el valor base (tray) para layouts 1xN
    // y trayTall para layouts 2xN (se duplica la altura para acomodar la
    // segunda fila de camaras manteniendo el mismo tamaño por celda).
    tray: 240,
    trayTall: 480,
    murphyDrawer: 300,
    // SmartSearch ocupa el leftstack completo cuando está abierto. Ancho
    // fijo equivalente a 2 sidebars apilados (mismo footprint visual).
    smartSearch: 560,
    // Radio TETRA vive en el slot derecho (split del drawer) y usa el
    // mismo ancho que el drawer normal — 340px por columna; con drawer
    // abierto el slot derecho duplica a 680px (igual que IncidentDispatch).
    radioTetra: 340,
  },
  // Query actual de la búsqueda inteligente (seteada por MapSearch al elegir
  // la opción "ai-search" del autocomplete). El panel la consume al abrir
  // para hacer el primer fetch. Vive aquí para que abrir el panel desde el
  // rail sin query no borre lo último que el usuario buscó.
  smartSearchQuery: '',
  // Modo inicial del panel SmartSearch al abrirse ('text' | 'face').
  // No persiste — vive solo durante la sesión; al recargar vuelve a 'text'.
  smartSearchInitialMode: 'text',
  // Filtro de jitter GPS para el trail de llamadas SOS. Cuando está
  // activo, un punto nuevo solo se agrega al trail si dista más de
  // sosTrailDenoiseMeters del último punto registrado. El marker sí se
  // mueve con cada update; solo el trail visible se filtra.
  sosTrailDenoise: ls('sosTrailDenoise', true),
  sosTrailDenoiseMeters: ls('sosTrailDenoiseMeters', 15),
  // Toggles por tipo de banner de alerta (toast rojo superior). Permite al
  // operador silenciar categorias puntuales (ej: "solo quiero ver SOS") sin
  // perder las alertas del sidebar — solo se oculta el banner flotante y su
  // audio. Merge defensivo con defaults: si una version nueva agrega un
  // tipo, los antiguos persistidos lo toman del default.
  bannerAlertTypes: (function() {
    const defaults = {
      incident: true,             // 9-1-1
      sos_call: true,             // llamada SOS entrante
      sensor_alert: true,         // panico sensor fisico
      external_sensor_alert: true, // panico sensor externo
      bus_alert: true,            // panico chofer autobus
      device_alert: true,         // panico chofer patrulla
      mc_alert: true,             // medida cautelar (fuga de arraigo, etc.)
    }
    return Object.assign({}, defaults, ls('bannerAlertTypes', {}))
  })(),
  // Radio (en metros) para "Recursos cercanos" en el drawer. El usuario
  // lo ajusta con un slider dentro de la misma sección del sidebar.
  nearbyRadius: ls('nearbyRadius', 500),
  // selectedItem se persiste para que el drawer vuelva a aparecer tras
  // recargar la pagina. El objeto guardado puede quedar stale si la
  // entidad cambia; en la practica los websockets refrescan posiciones
  // al cargar, y si la entidad desaparece el usuario cierra el drawer.
  selectedItem: ls('selectedItem', null),
  // Usuario dueño del estado del operador persistido (selectedItem, pines,
  // pines Murphy, seguimiento). Lo reclama MapV2 al montar con
  // CLAIM_OPERATOR_STATE: si es otro, ese estado se descarta.
  operatorStateOwner: ls('operatorStateOwner', null),
  // Unidad en seguimiento, persistida para que un F5 no lo pierda. Un
  // seguimiento suele arrancar porque algo esta pasando; si el navegador se
  // congela y el operador recarga, volver a buscar la unidad a mano es
  // justamente lo que no debe tener que hacer.
  //
  // Shape: { monitorId, entityType, coords: [[lng,lat], ...], startedAt }
  //
  // `coords` guarda el trazo acumulado desde que empezo el seguimiento — no
  // basta el id, porque al restaurar el rastro arrancaria vacio y se perderia
  // el recorrido que el operador llevaba observando. Se capea a 200 puntos:
  // el rastro en vivo admite 500, pero localStorage no es lugar para eso y
  // 200 ya cubren un tramo largo.
  followedUnit: ls('followedUnit', null),
  faceSearchFilters: ls('faceSearchFilters', {
    mode: 'all',
    selectedCity: null,
    selectedRadius: null,
    startDate: null,
    startTime: null,
    endDate: null,
    endTime: null,
    minSimilarity: 80,
  }),
  // Tray state:
  // - layout: uno de 1x4|1x8|1x12|2x4|2x8|2x12 (cols x rows visibles por ventana)
  // - cameras: array de cámaras pineadas, cap global MAX_PINNED
  // - mode: docked (barra) | floating (inline resizable) | popout (ventana separada) | fullscreen (videowall)
  // - previousMode: modo al que volver cuando salimos de un modo especial (popout/fullscreen).
  //   Solo guarda modos normales (docked/floating) — ver SET_TRAY_MODE.
  // - floatingPos: {x, y} posición cuando mode === 'floating'
  // - floatingSize: {w, h} dimensiones custom del modo flotante (null = auto)
  // Merge defensivo: los defaults van primero, luego el valor persistido
  // los sobrescribe. Asi, si el state guardado en localStorage es viejo
  // (sin campos nuevos como previousMode), los campos faltantes toman el
  // default automaticamente. Ademas, un guard post-merge garantiza que
  // previousMode siempre sea un modo normal (nunca undefined ni especial).
  tray: (function() {
    const defaults = {
      layout: '1x4',          // legacy — solo para back-compat de lectura/migración
      cameras: [],
      mode: 'docked',
      previousMode: 'docked',
      floatingPos: { x: 100, y: 100 },
      floatingSize: { w: null, h: null },
    }
    const merged = { ...defaults, ...ls('tray', {}) }
    const SPECIAL = ['popout', 'fullscreen']
    if (!merged.previousMode || SPECIAL.includes(merged.previousMode)) {
      merged.previousMode = 'docked'
    }
    // Mosaico: usa el grid persistido si existe, si no migra desde layout legacy.
    merged.grid = migrateTrayGrid(merged, merged.grid || null)
    // Garantizar sincronía tiles ↔ cameras tras un reload.
    merged.grid = { ...merged.grid, tiles: reconcileTiles(merged.cameras, merged.grid.tiles) }
    return merged
  })(),
  // Pines de items seleccionados desde el drawer. Cada entry preserva
  // la ultima data conocida para sobrevivir reloads cuando el WS todavia
  // no sincroniza. Se rehidrata desde this._monitors en MapV2.vue al
  // recibir updates via _ingestEntities / _ingestSingleEntity.
  // Cap 10 FIFO: al pinear el 11° se descarta el mas antiguo.
  pinnedItems: ls('pinnedItems', []),
  // Pines Murphy. Ahora persisten la data completa (data, lprData,
  // personas, view state del paginador y filtros) para poder rehidratar
  // el resultado al reload sin pedir al operador que vuelva a consultar
  // Murphy. Cap 10 FIFO.
  pinnedMurphyItems: ls('pinnedMurphyItems', []),
  // Id del pin Murphy que estaba activo al cerrar la sesion. Al cargar
  // el modulo hidratamos murphyResult desde ese pin, para que al recargar
  // se vea la misma ficha sin intervencion del operador.
  activeMurphyPinId: ls('activeMurphyPinId', null),
  // Presets de video wall. Persistidos POR USUARIO en claves separadas
  // (kalan:map-v2:trayPresets:<userId>) — NUNCA dentro de `tray` (se
  // re-serializa en cada tick de drag). _userId se setea en login (auth.js).
  // Forma: { id, name, createdAt, cols, tiles:[{cameraRef:{id,devId}, w, h, pinned?}] }
  _userId: ls('trayPresetsUserId', null),
  trayPresets: [],
  activePresetId: null,
  // Flag de divergencia: true cuando el tray cambió respecto al preset activo
  // sin haber guardado. Permite "Guardar cambios en «X»" sin perder cuál es X.
  activePresetDirty: false,
  // Resultado Murphy activo en memoria. Se inicializa abajo desde el pin
  // con id activeMurphyPinId (si existe) para sobrevivir al reload.
  murphyResult: null,
  wsConnected: false,
}

// Hidratar murphyResult desde el pin activo (si existe). Esto se ejecuta
// una sola vez al cargar el modulo.
;(function hydrateActiveMurphyResult() {
  if (!state.activeMurphyPinId) return
  const pin = state.pinnedMurphyItems.find(p => p.id === state.activeMurphyPinId)
  if (!pin) {
    state.activeMurphyPinId = null
    lsSet('activeMurphyPinId', null)
    return
  }
  state.murphyResult = {
    id: pin.id,
    kind: pin.kind,
    title: pin.title,
    queryName: pin.queryName,
    queryParams: pin.queryParams,
    data: pin.data || null,
    lprData: pin.lprData || [],
    personas: pin.personas || [],
    fetchedAt: pin.fetchedAt || pin.pinnedAt,
    status: 'fresh',
    errorMessage: null,
    personaIdx: pin.personaIdx || 0,
    filterEstado: pin.filterEstado || null,
    filterFecha: pin.filterFecha || '',
    filtersVisible: !!pin.filtersVisible,
  }
})()

;(function hydrateTrayPresets() {
  if (!state._userId) return
  state.trayPresets = ls('trayPresets:' + state._userId, [])
  state.activePresetId = ls('activePresetId:' + state._userId, null)
  state.activePresetDirty = ls('activePresetDirty:' + state._userId, false)
})()

// Marca el tray como divergido del preset activo SIN olvidar cuál era (para
// poder "Guardar cambios en «X»"). Lo usan las mutations que cambian el tray.
function markTrayDirty(state) {
  if (state.activePresetId && !state.activePresetDirty) {
    state.activePresetDirty = true
    if (state._userId) lsSet('activePresetDirty:' + state._userId, true)
  }
}

// Resetea por completo el vínculo con el preset activo (al vaciar el tray o
// cambiar de usuario). No queda preset activo ni estado "modificado".
function resetActivePreset(state) {
  state.activePresetId = null
  state.activePresetDirty = false
  if (state._userId) {
    lsSet('activePresetId:' + state._userId, null)
    lsSet('activePresetDirty:' + state._userId, false)
  }
}

// Sync de presets con apiv2 en vuelo: { uid, promise, pendingOps }. Las
// escrituras al server esperan a que termine (un GET tardío no debe pisar un
// preset recién guardado) y las ops locales hechas mientras tanto se
// re-aplican sobre la lista del server.
let presetSync = null

function trackPendingPresetOp(op) {
  if (presetSync) presetSync.pendingOps.push(op)
}

// Escritura al server en background: la UI ya se actualizó localmente. Se
// descarta si cambió el usuario (la cookie ya sería de otro operador).
function pushPresetWrite(state, label, fn) {
  const uid = state._userId
  if (uid == null) return Promise.resolve()
  const wait = presetSync ? presetSync.promise : Promise.resolve()
  return wait
    .then(() => (state._userId === uid ? fn() : null))
    .catch((err) => {
      console.error('[tray-presets] ' + label, { err })
      if (window.VMA) window.VMA.showError({ title: 'No se pudo guardar el preset en el servidor' })
    })
}

const mutations = {
  SET_VIEWPORT(state, viewport) {
    state.viewport = { ...state.viewport, ...viewport }
    lsSet('viewport', state.viewport)
  },

  SET_PANEL(state, { panel, value }) {
    // Exclusividad del leftstack: SmartSearch ocupa todo el ancho del
    // leftstack y no coexiste con sidebar/sosLeft/murphyDrawer.
    // Al abrir uno, se colapsa el otro automáticamente. Los demás paneles
    // (drawer derecho, tray inferior) NO están sujetos a esta regla.
    var LEFTSTACK = ['sidebar', 'sosLeft', 'murphyDrawer']
    var next = { ...state.panels, [panel]: value }
    if (value === true) {
      if (panel === 'smartSearch') {
        for (var i = 0; i < LEFTSTACK.length; i++) next[LEFTSTACK[i]] = false
      } else if (LEFTSTACK.indexOf(panel) !== -1) {
        next.smartSearch = false
      }
    }
    state.panels = next
    lsSet('panels', state.panels)
  },

  SET_SMART_SEARCH_QUERY(state, query) {
    state.smartSearchQuery = query || ''
  },

  SET_SMART_SEARCH_INITIAL_MODE(state, mode) {
    state.smartSearchInitialMode = mode || 'text'
  },

  SET_NEW_INCIDENT_SEED(state, seed) {
    state.newIncidentSeed = seed || null
  },

  SET_DISPATCH_TARGET(state, incidentId) {
    state.dispatchTargetIncidentId = incidentId || null
  },

  // ───────── Radio TETRA mutations ─────────
  SET_RADIO_TETRA_TARGET(state, target) {
    state.radioTetraTarget = target || null
  },
  SET_RADIO_TETRA_MODE(state, mode) {
    // El modo 'multi' fue eliminado — colapsamos al 'channel' multi-select.
    var allowed = ['device', 'channel']
    var resolved = mode === 'multi' ? 'channel' : mode
    state.radioTetraMode = allowed.indexOf(resolved) !== -1 ? resolved : 'channel'
  },
  // SET_RADIO_TALKING / SET_RADIO_ACTIVE_CHANNELS / SET_RADIO_CHANNELS
  // eliminadas — el state real vive en el store radioCall (active.floor +
  // active.peerId) y radioGroups (visibleGroups del backend).
  ADD_RADIO_PARTICIPANT(state, device) {
    if (!device || !device.uniqueId) return
    var exists = state.radioTetraParticipants.some(function(p) {
      return p.uniqueId === device.uniqueId
    })
    if (exists) return
    state.radioTetraParticipants = state.radioTetraParticipants.concat([{
      monitorId: device.monitorId || null,
      uniqueId: device.uniqueId,
      name: device.name || device.uniqueId,
      groupName: device.groupName || '',
    }])
  },
  REMOVE_RADIO_PARTICIPANT(state, uniqueId) {
    if (!uniqueId) return
    state.radioTetraParticipants = state.radioTetraParticipants.filter(function(p) {
      return p.uniqueId !== uniqueId
    })
  },
  CLEAR_RADIO_PARTICIPANTS(state) {
    state.radioTetraParticipants = []
  },
  PUSH_RADIO_ACTIVITY(state, entry) {
    if (!entry) return
    var next = [entry].concat(state.radioActivityLog)
    if (next.length > 50) next = next.slice(0, 50)
    state.radioActivityLog = next
  },
  // Actualiza una entry existente por id. Usado para parchar el transcript
  // de un TX cuando llega texto tardío del SpeechRecognition después de
  // que el commit inicial ya pasó.
  UPDATE_RADIO_ACTIVITY(state, patch) {
    if (!patch || !patch.id) return
    state.radioActivityLog = state.radioActivityLog.map(function(e) {
      if (e.id === patch.id) return Object.assign({}, e, patch)
      return e
    })
  },
  CLEAR_RADIO_ACTIVITY(state) {
    state.radioActivityLog = []
  },

  // Reemplaza el raw del incidente seleccionado con la versión fresca
  // devuelta por el backend (después de cancel/close/assign/change-status).
  // Patrón equivalente a setSelectedIncident(data) del legacy: el backend
  // es la fuente de verdad y devuelve el incidente entero en cada acción.
  // Solo se aplica si el id coincide con el incidente actualmente seleccionado,
  // para evitar pisar otra selección que el operador hizo mientras llegaba
  // la respuesta tardía.
  // Genérico: refresca el raw del item seleccionado (cualquier entityType)
  // cuando llega una versión más fresca (backend o WS). Solo aplica si el id
  // coincide con el seleccionado, para no pisar otra selección. Reemplaza el
  // raw entero (no merge) para que los campos removidos también desaparezcan.
  REFRESH_SELECTED_ITEM_RAW(state, raw) {
    if (!raw || !state.selectedItem || !state.selectedItem.data) return
    var currentRaw = state.selectedItem.data.data
    var rawId = raw.id != null ? raw.id : raw._id
    var curId = currentRaw && (currentRaw.id != null ? currentRaw.id : currentRaw._id)
    if (rawId == null || curId == null) return
    if (String(rawId) !== String(curId)) return
    state.selectedItem.data.data = raw
    lsSet('selectedItem', state.selectedItem)
  },

  START_LOCATION_PICKER(state, payload) {
    var prompt = (payload && payload.prompt) || 'Haz clic en el mapa para seleccionar la ubicación'
    state.locationPicker = {
      active: true,
      prompt: prompt,
      result: null,
      requestId: (state.locationPicker.requestId || 0) + 1,
    }
  },

  RESOLVE_LOCATION_PICKER(state, result) {
    // Mantiene el requestId para que el watcher del consumidor distinga
    // entre múltiples invocaciones aún si las coords son las mismas.
    state.locationPicker = {
      active: false,
      prompt: '',
      result: result || null,
      requestId: state.locationPicker.requestId,
    }
  },

  CLEAR_LOCATION_PICKER(state) {
    state.locationPicker = {
      active: false,
      prompt: '',
      result: null,
      requestId: state.locationPicker.requestId,
    }
  },

  SET_KPI_CATEGORY_FILTER(state, category) {
    state.kpiCategoryFilter = category || null
  },

  SET_LAYER_VISIBILITY(state, { entityType, visible }) {
    state.layerVisibility = { ...state.layerVisibility, [entityType]: visible }
    lsSet('layerVisibility', state.layerVisibility)
  },

  SET_ENTITY_FILTER(state, { entityType, filters }) {
    state.entityFilters = {
      ...state.entityFilters,
      [entityType]: { ...(state.entityFilters[entityType] || {}), ...filters },
    }
    lsSet('entityFilters', state.entityFilters)
  },

  SET_GEOFENCES_VISIBLE(state, value) {
    // Legacy: antes setaba un boolean global; ahora mapea a prender/apagar
    // los 4 filtros. Preservamos la firma para codigo existente.
    var enabled = !!value
    var next = {
      geofence: enabled,
      region: enabled,
      sector: enabled,
      cuadrante: enabled,
    }
    state.geofenceFilters = next
    lsSet('geofenceFilters', next)
  },

  SET_GEOFENCE_FILTER(state, payload) {
    if (!payload || !payload.type) return
    var next = Object.assign({}, state.geofenceFilters)
    next[payload.type] = !!payload.enabled
    state.geofenceFilters = next
    lsSet('geofenceFilters', next)
  },

  SET_ALL_GEOFENCE_FILTERS(state, enabled) {
    var keys = Object.keys(state.geofenceFilters)
    var next = {}
    for (var i = 0; i < keys.length; i++) next[keys[i]] = !!enabled
    state.geofenceFilters = next
    lsSet('geofenceFilters', next)
  },

  SET_HEATMAP_ACTIVE(state, value) {
    state.heatmapActive = value
    lsSet('heatmapActive', value)
  },

  SET_JURISDICTION_OUTLINE_VISIBLE(state, value) {
    state.jurisdictionOutlineVisible = !!value
    lsSet('jurisdictionOutlineVisible', state.jurisdictionOutlineVisible)
  },

  SET_EXTERNAL_LABELS_ATTENUATED(state, value) {
    state.externalLabelsAttenuated = !!value
    lsSet('externalLabelsAttenuated', state.externalLabelsAttenuated)
  },

  SET_LABEL_MODE(state, value) {
    var safe = (value === 'dense' || value === 'minimal') ? value : 'smart'
    state.labelMode = safe
    lsSet('labelMode', safe)
  },

  SET_CLUSTER_SETTINGS(state, { enabled, maxZoom }) {
    if (enabled !== undefined) {
      state.clusterEnabled = enabled
      lsSet('clusterEnabled', enabled)
    }
    if (maxZoom !== undefined) {
      state.clusterMaxZoom = maxZoom
      lsSet('clusterMaxZoom', maxZoom)
    }
  },

  SET_SOS_TRAIL_DENOISE(state, { enabled, meters }) {
    if (enabled !== undefined) {
      state.sosTrailDenoise = enabled
      lsSet('sosTrailDenoise', enabled)
    }
    if (meters !== undefined) {
      state.sosTrailDenoiseMeters = meters
      lsSet('sosTrailDenoiseMeters', meters)
    }
  },

  // Toggle individual de un tipo de banner. Nueva referencia de objeto
  // para que los computed que leen bannerAlertTypes reaccionen — Vue 2
  // no detecta mutaciones in-place en propiedades ya existentes.
  SET_BANNER_ALERT_TYPE(state, payload) {
    if (!payload || !payload.type) return
    var next = Object.assign({}, state.bannerAlertTypes)
    next[payload.type] = !!payload.enabled
    state.bannerAlertTypes = next
    lsSet('bannerAlertTypes', next)
  },

  // Bulk: activa/desactiva todos los tipos de una vez (botones "Todo"/"Ninguno").
  SET_ALL_BANNER_ALERT_TYPES(state, enabled) {
    var next = {}
    var keys = Object.keys(state.bannerAlertTypes)
    for (var i = 0; i < keys.length; i++) next[keys[i]] = !!enabled
    state.bannerAlertTypes = next
    lsSet('bannerAlertTypes', next)
  },

  SET_NEARBY_RADIUS(state, value) {
    var clamped = Math.max(0, Math.min(2000, value || 0))
    state.nearbyRadius = clamped
    lsSet('nearbyRadius', clamped)
  },

  SET_SELECTED_ITEM(state, item) {
    state.selectedItem = item
    lsSet('selectedItem', item)
  },

  // El estado del operador vive en localStorage sin dueño. La limpieza de
  // SET_LOGIN depende de `lastUser`, que solo escribe el logout explícito: con
  // una sesión expirada o una pestaña cerrada, el siguiente usuario heredaba la
  // selección (medido en prod 2026-10-13: Ecatepec abrió el mapa con el drawer
  // de una unidad de Ixtapaluca/Chalco). MapV2 lo reclama al montar; sin dueño
  // registrado (estado previo a este campo) también se descarta.
  CLAIM_OPERATOR_STATE(state, userId) {
    if (userId == null) return
    if (state.operatorStateOwner != null && String(state.operatorStateOwner) === String(userId)) return
    state.selectedItem = null
    lsSet('selectedItem', null)
    state.pinnedItems = []
    lsSet('pinnedItems', [])
    state.pinnedMurphyItems = []
    lsSet('pinnedMurphyItems', [])
    state.activeMurphyPinId = null
    lsSet('activeMurphyPinId', null)
    state.murphyResult = null
    state.followedUnit = null
    lsSet('followedUnit', null)
    state.operatorStateOwner = userId
    lsSet('operatorStateOwner', userId)
  },

  // Arranca o reemplaza el seguimiento persistido.
  SET_FOLLOWED_UNIT(state, payload) {
    state.followedUnit = payload
    lsSet('followedUnit', payload)
  },

  // Extiende el trazo persistido. Se llama con throttle desde la vista: en
  // cada posicion GPS seria una escritura a localStorage por segundo y por
  // unidad, que es serializar el arreglo entero cada vez.
  SET_FOLLOWED_COORDS(state, coords) {
    if (!state.followedUnit) return
    var cap = Array.isArray(coords) && coords.length > 200
      ? coords.slice(-200)
      : coords
    state.followedUnit = Object.assign({}, state.followedUnit, { coords: cap })
    lsSet('followedUnit', state.followedUnit)
  },

  CLEAR_FOLLOWED_UNIT(state) {
    state.followedUnit = null
    lsSet('followedUnit', null)
  },

  // Aplica un shallow merge sobre el raw del backend del item seleccionado.
  // Útil para optimistic updates desde acciones (descartar alerta, cerrar,
  // generar alerta) que cambian el estado del item antes de que llegue el
  // WS update que confirma el nuevo status.
  PATCH_SELECTED_ITEM_RAW(state, patch) {
    if (!patch || !state.selectedItem || !state.selectedItem.data) return
    var current = state.selectedItem.data.data
    if (!current) return
    state.selectedItem.data.data = Object.assign({}, current, patch)
    lsSet('selectedItem', state.selectedItem)
  },

  PIN_CURRENT(state) {
    if (!state.selectedItem || !state.selectedItem.monitorId) return
    var id = state.selectedItem.monitorId
    var already = state.pinnedItems.some(function(p) { return p.monitorId === id })
    if (already) return
    var pinned = state.pinnedItems.slice()
    var MAX_PINS = 10
    if (pinned.length >= MAX_PINS) pinned.shift()
    pinned.push({
      monitorId: state.selectedItem.monitorId,
      entityType: state.selectedItem.entityType,
      data: state.selectedItem.data,
      pinnedAt: Date.now(),
    })
    state.pinnedItems = pinned
    lsSet('pinnedItems', pinned)
  },

  UNPIN_ITEM(state, monitorId) {
    state.pinnedItems = state.pinnedItems.filter(function(p) { return p.monitorId !== monitorId })
    lsSet('pinnedItems', state.pinnedItems)
  },

  REPLACE_PINNED(state, { monitorId, data, entityType }) {
    var idx = state.pinnedItems.findIndex(function(p) { return p.monitorId === monitorId })
    if (idx === -1) return
    var updated = state.pinnedItems.slice()
    updated[idx] = {
      ...updated[idx],
      data: data,
      entityType: entityType || updated[idx].entityType,
    }
    state.pinnedItems = updated
    lsSet('pinnedItems', updated)
  },

  CLEAR_ALL_PINS(state) {
    state.pinnedItems = []
    lsSet('pinnedItems', [])
  },

  // Purga del estado persistido lo que el operador ya no puede ver.
  // selectedItem y pinnedItems se rehidratan desde localStorage sin pasar por
  // la ingesta, asi que el gate de permisos tiene que alcanzarlos aparte.
  // La invoca MapV2.created(), antes de que el drawer llegue a pintarse.
  PURGE_FORBIDDEN_STATE(state, { canSeeReportants }) {
    var purged = purgeReportantState(state, canSeeReportants)
    // El summary del KPI se persiste completo y su bloque `incidents` guarda
    // las categorias del 9-1-1 y el array crudo. Fuera del localStorage para
    // todos, con o sin permiso — mismo criterio que murphyResult. El panel
    // sigue abriendo con el resto y recupera el desglose al refrescar.
    var stripped = stripKpiIncidents(purged.selectedItem)
    if (stripped !== state.selectedItem) {
      state.selectedItem = stripped
      lsSet('selectedItem', stripped)
    }
    if (purged.pinnedItems.length !== state.pinnedItems.length) {
      state.pinnedItems = purged.pinnedItems
      lsSet('pinnedItems', purged.pinnedItems)
    }
  },

  // Purga del estado persistido de capas que el perfil no permite (Parte D):
  // selectedItem, pinnedItems, unidad seguida y cámaras del tray. Los presets
  // del video wall no se tocan: al aplicarlos se resuelven contra las cámaras
  // vivas, que no se cargan si la capa no está permitida.
  PURGE_FORBIDDEN_LAYERS(state, { permitidas }) {
    var purged = purgarEstadoDeCapas(state, permitidas)
    if (purged.selectedItem !== state.selectedItem) {
      state.selectedItem = purged.selectedItem
      lsSet('selectedItem', purged.selectedItem)
    }
    if (purged.pinnedItems.length !== state.pinnedItems.length) {
      state.pinnedItems = purged.pinnedItems
      lsSet('pinnedItems', purged.pinnedItems)
    }
    if (purged.followedUnit !== state.followedUnit) {
      state.followedUnit = purged.followedUnit
      lsSet('followedUnit', purged.followedUnit)
    }
    if (!capaPermitida(permitidas, 'camera') && state.tray.cameras.length) {
      var grid = { ...state.tray.grid, tiles: reconcileTiles([], state.tray.grid.tiles) }
      state.tray = { ...state.tray, cameras: [], grid: grid }
      lsSet('tray', state.tray)
      resetActivePreset(state)
    }
  },

  SET_TRAY_COLS(state, cols) {
    const c = [2, 3, 4, 6, 8, 12].includes(cols) ? cols : 4
    if (c === state.tray.grid.cols) return
    state.tray = { ...state.tray, grid: { ...state.tray.grid, cols: c } }
    lsSet('tray', state.tray)
    markTrayDirty(state)
  },

  SET_TRAY_TILES(state, tiles) {
    const synced = reconcileTiles(state.tray.cameras, tiles)
    state.tray = { ...state.tray, grid: { ...state.tray.grid, tiles: synced } }
    lsSet('tray', state.tray)
    markTrayDirty(state)
  },

  LOAD_PRESETS_FOR_USER(state, userId) {
    state._userId = userId || null
    lsSet('trayPresetsUserId', state._userId)
    state.trayPresets = userId ? ls('trayPresets:' + userId, []) : []
    state.activePresetId = userId ? ls('activePresetId:' + userId, null) : null
    state.activePresetDirty = userId ? ls('activePresetDirty:' + userId, false) : false
  },

  // Garantiza que _userId esté seteado al usar presets (sin depender de que
  // SET_LOGIN haya corrido — p.ej. sesión ya iniciada antes de este código).
  // Si ya hay presets persistidos para el usuario, los carga; si no, persiste
  // lo que haya en memoria bajo la clave del usuario (no pierde lo recién hecho).
  ENSURE_PRESETS_USER(state, userId) {
    if (!userId || state._userId === userId) return
    const prevUserId = state._userId
    state._userId = userId
    lsSet('trayPresetsUserId', userId)
    const stored = ls('trayPresets:' + userId, null)
    if (stored !== null) {
      state.trayPresets = stored
      state.activePresetId = ls('activePresetId:' + userId, null)
      state.activePresetDirty = ls('activePresetDirty:' + userId, false)
    } else if (prevUserId != null) {
      // Lo que hay en memoria es de OTRO operador (turnos rotativos sin
      // logout): no se hereda. Si se persistiera aquí, syncTrayPresets lo
      // subiría al server bajo la cuenta equivocada.
      state.trayPresets = []
      state.activePresetId = null
      state.activePresetDirty = false
      lsSet('trayPresets:' + userId, [])
      lsSet('activePresetId:' + userId, null)
      lsSet('activePresetDirty:' + userId, false)
    } else {
      lsSet('trayPresets:' + userId, state.trayPresets)
      lsSet('activePresetId:' + userId, state.activePresetId)
      lsSet('activePresetDirty:' + userId, state.activePresetDirty)
    }
  },

  SAVE_TRAY_PRESET(state, preset) {
    const MAX_PRESETS = 20
    const idx = state.trayPresets.findIndex(p => p.id === preset.id)
    const list = state.trayPresets.slice()
    if (idx >= 0) {
      list[idx] = preset
    } else {
      if (list.length >= MAX_PRESETS) list.shift()
      list.push(preset)
    }
    state.trayPresets = list
    state.activePresetId = preset.id
    state.activePresetDirty = false
    if (state._userId) {
      lsSet('trayPresets:' + state._userId, list)
      lsSet('activePresetId:' + state._userId, preset.id)
      lsSet('activePresetDirty:' + state._userId, false)
    }
  },

  DELETE_TRAY_PRESET(state, id) {
    state.trayPresets = state.trayPresets.filter(p => p.id !== id)
    if (state.activePresetId === id) {
      state.activePresetId = null
      state.activePresetDirty = false
    }
    if (state._userId) {
      lsSet('trayPresets:' + state._userId, state.trayPresets)
      lsSet('activePresetId:' + state._userId, state.activePresetId)
      lsSet('activePresetDirty:' + state._userId, state.activePresetDirty)
    }
  },

  // Adopta la lista del server (sync). Mantiene la caché LS y suelta el
  // vínculo si el preset activo ya no existe (p.ej. borrado en otro origen).
  SET_TRAY_PRESETS(state, list) {
    state.trayPresets = list
    if (state.activePresetId && !list.some(p => p.id === state.activePresetId)) {
      resetActivePreset(state)
    }
    if (state._userId) lsSet('trayPresets:' + state._userId, list)
  },

  APPLY_TRAY_PRESET(state, { presetId, cameras, grid }) {
    state.tray = { ...state.tray, cameras, grid }
    lsSet('tray', state.tray)
    state.activePresetId = presetId
    state.activePresetDirty = false
    if (state._userId) {
      lsSet('activePresetId:' + state._userId, presetId)
      lsSet('activePresetDirty:' + state._userId, false)
    }
  },

  PIN_CAMERA(state, camera) {
    // Cap global fijo (como argos). El grid ya no descarta cámaras —
    // determina solo cuántas se ven en viewport y el container hace scroll.
    const MAX_PINNED = 36
    const already = state.tray.cameras.find(c => c.id === camera.id)
    if (already) return
    const cameras = [...state.tray.cameras]
    if (cameras.length >= MAX_PINNED) cameras.shift()
    cameras.push(camera)
    const tiles = reconcileTiles(cameras, state.tray.grid.tiles)
    state.tray = { ...state.tray, cameras, grid: { ...state.tray.grid, tiles } }
    lsSet('tray', state.tray)
    markTrayDirty(state)
  },

  SET_TRAY_MODE(state, mode) {
    // Lista de modos "especiales" (overlay/ventana): popout, fullscreen.
    // Los modos normales son docked y floating.
    // Cuando entramos a un modo especial DESDE un modo normal, guardamos el
    // modo normal como previousMode para poder volver a el al salir.
    // Cuando nos movemos entre modos normales, actualizamos normalmente.
    // Cuando nos movemos entre modos especiales (raro: popout -> fullscreen),
    // NO tocamos previousMode — asi preservamos el normal original.
    const SPECIAL = ['popout', 'fullscreen']
    const wasSpecial = SPECIAL.includes(state.tray.mode)
    const goingSpecial = SPECIAL.includes(mode)
    let previousMode = state.tray.previousMode
    if (goingSpecial && !wasSpecial) {
      // Entrando a especial desde normal: recordar de donde venimos
      previousMode = state.tray.mode
    } else if (!goingSpecial) {
      // Yendo a un modo normal: ese es ahora nuestro "previo" de referencia
      previousMode = mode
    }
    // else: ambos especiales, no tocar previousMode
    state.tray = { ...state.tray, mode, previousMode }
    lsSet('tray', state.tray)
  },

  SET_TRAY_FLOATING_POS(state, pos) {
    state.tray = { ...state.tray, floatingPos: { ...state.tray.floatingPos, ...pos } }
    lsSet('tray', state.tray)
  },

  SET_TRAY_FLOATING_SIZE(state, size) {
    state.tray = {
      ...state.tray,
      floatingSize: { ...(state.tray.floatingSize || {}), ...size },
    }
    lsSet('tray', state.tray)
  },

  UNPIN_CAMERA(state, cameraId) {
    const cameras = state.tray.cameras.filter(c => c.id !== cameraId)
    const tiles = reconcileTiles(cameras, state.tray.grid.tiles)
    state.tray = { ...state.tray, cameras, grid: { ...state.tray.grid, tiles } }
    lsSet('tray', state.tray)
    // Si se vació el tray, desvincular el preset (no dejar "X (modificado)" sobre
    // un tray vacío, que permitiría guardar un preset sin cámaras).
    if (cameras.length === 0) resetActivePreset(state)
    else markTrayDirty(state)
  },

  CLEAR_TRAY(state) {
    state.tray = { ...state.tray, cameras: [], grid: { ...state.tray.grid, tiles: [] } }
    lsSet('tray', state.tray)
    resetActivePreset(state)
  },

  SET_WS_CONNECTED(state, value) {
    state.wsConnected = value
  },

  SET_MURPHY_RESULT(state, result) {
    state.murphyResult = result
  },

  SET_MURPHY_STATUS(state, { status, errorMessage }) {
    if (!state.murphyResult) return
    var next = { ...state.murphyResult, status: status }
    if (errorMessage !== undefined) next.errorMessage = errorMessage
    state.murphyResult = next
  },

  PIN_CURRENT_MURPHY(state) {
    if (!state.murphyResult || !state.murphyResult.id) return
    var r = state.murphyResult
    var id = r.id
    var already = state.pinnedMurphyItems.some(function(p) { return p.id === id })
    if (already) return
    var pinned = state.pinnedMurphyItems.slice()
    var MAX_PINS = 10
    if (pinned.length >= MAX_PINS) pinned.shift()
    pinned.push({
      id: r.id,
      kind: r.kind,
      title: r.title,
      queryName: r.queryName,
      queryParams: r.queryParams,
      pinnedAt: Date.now(),
      // Snapshot completo de la data y del view state para rehidratar al
      // reload sin requerir refetch manual.
      data: r.data || null,
      roboData: r.roboData || null,
      placasAsociadas: r.placasAsociadas || [],
      lprData: r.lprData || [],
      personas: r.personas || [],
      fetchedAt: r.fetchedAt || Date.now(),
      personaIdx: r.personaIdx || 0,
      filterEstado: r.filterEstado || null,
      filterFecha: r.filterFecha || '',
      filtersVisible: !!r.filtersVisible,
    })
    state.pinnedMurphyItems = pinned
    lsSet('pinnedMurphyItems', pinned)
    // Marcar este pin como el activo al pinear — asi al reload vuelve a
    // estar visible sin necesidad de reseleccion.
    state.activeMurphyPinId = id
    lsSet('activeMurphyPinId', id)
  },

  UNPIN_MURPHY(state, id) {
    state.pinnedMurphyItems = state.pinnedMurphyItems.filter(function(p) {
      return p.id !== id
    })
    lsSet('pinnedMurphyItems', state.pinnedMurphyItems)
    if (state.activeMurphyPinId === id) {
      state.activeMurphyPinId = null
      lsSet('activeMurphyPinId', null)
    }
  },

  REPLACE_PINNED_MURPHY_TITLE(state, { id, title }) {
    var idx = state.pinnedMurphyItems.findIndex(function(p) { return p.id === id })
    if (idx === -1) return
    var updated = state.pinnedMurphyItems.slice()
    updated[idx] = { ...updated[idx], title: title }
    state.pinnedMurphyItems = updated
    lsSet('pinnedMurphyItems', updated)
  },

  // Actualiza campos arbitrarios del pin persistido. Se usa para:
  //   - Sincronizar el snapshot de data/lprData/personas tras un refetch
  //     manual (action refetchMurphyResult).
  //   - Persistir el view state (personaIdx, filtros) cuando el operador
  //     cambia el paginador de homonimos o los filtros aplicados.
  UPDATE_PINNED_MURPHY(state, { id, patch }) {
    var idx = state.pinnedMurphyItems.findIndex(function(p) { return p.id === id })
    if (idx === -1) return
    var updated = state.pinnedMurphyItems.slice()
    updated[idx] = { ...updated[idx], ...patch }
    state.pinnedMurphyItems = updated
    lsSet('pinnedMurphyItems', updated)
  },

  SET_ACTIVE_MURPHY_PIN(state, id) {
    state.activeMurphyPinId = id || null
    lsSet('activeMurphyPinId', state.activeMurphyPinId)
  },

  CLEAR_ALL_MURPHY_PINS(state) {
    state.pinnedMurphyItems = []
    lsSet('pinnedMurphyItems', [])
    state.activeMurphyPinId = null
    lsSet('activeMurphyPinId', null)
  },

  SET_FACE_SEARCH_FILTERS(state, filters) {
    state.faceSearchFilters = { ...state.faceSearchFilters, ...filters }
    lsSet('faceSearchFilters', state.faceSearchFilters)
  },
  RESET_FACE_SEARCH_FILTERS(state) {
    state.faceSearchFilters = {
      mode: 'all',
      selectedCity: null,
      selectedRadius: null,
      startDate: null,
      startTime: null,
      endDate: null,
      endTime: null,
      minSimilarity: 80,
    }
    lsSet('faceSearchFilters', state.faceSearchFilters)
  },
}

const actions = {
  togglePanel({ commit, state }, panel) {
    const newValue = !state.panels[panel]
    commit('SET_PANEL', { panel, value: newValue })
  },

  // Abre el panel de SmartSearch precargando una query y opcionalmente un modo.
  // Retro-compatible: si payload es string, se trata como query (modo 'text').
  // Si payload es objeto { query, mode }, se usan ambos campos.
  openSmartSearch({ commit }, payload) {
    const query = typeof payload === 'string' ? payload : (payload && payload.query) || ''
    const mode = (payload && payload.mode) || 'text'
    commit('SET_SMART_SEARCH_QUERY', query)
    commit('SET_SMART_SEARCH_INITIAL_MODE', mode)
    commit('SET_PANEL', { panel: 'smartSearch', value: true })
  },

  // Abre el panel "Crear incidente" precargando coords del seed (típicamente
  // desde el menú contextual del mapa). Payload: { latitude, longitude }.
  openNewIncident({ commit }, payload) {
    commit('SET_NEW_INCIDENT_SEED', payload || null)
    commit('SET_PANEL', { panel: 'newIncident', value: true })
  },

  closeNewIncident({ commit }) {
    commit('SET_PANEL', { panel: 'newIncident', value: false })
    commit('SET_NEW_INCIDENT_SEED', null)
  },

  // Abre el panel "Despacho" para el incidente indicado. El panel vive en
  // un split del drawer derecho (sidebar 2x ancho), pane izq = despacho,
  // pane der = el drawer normal del incidente.
  openIncidentDispatch({ commit }, payload) {
    var id = (payload && (payload.incidentId || payload.id)) || payload || null
    commit('SET_DISPATCH_TARGET', id)
    commit('SET_PANEL', { panel: 'incidentDispatch', value: true })
  },

  closeIncidentDispatch({ commit }) {
    commit('SET_PANEL', { panel: 'incidentDispatch', value: false })
    commit('SET_DISPATCH_TARGET', null)
  },

  // ───────── Radio TETRA actions ─────────
  //
  // Abre el panel de Radio TETRA. El target puede ser:
  //   - null/undefined → panel en modo canal libre (sin device específico)
  //   - { type:'device', monitorId, uniqueId, name, groupName }
  //   - { type:'channel' } → modo canal por default
  //
  // El panel TETRA vive en el slot derecho del grid como split del drawer
  // normal. Es EXCLUSIVO con incidentDispatch y newIncident (no puede haber
  // dos paneles en el split derecho) — cerrarlos al abrir TETRA. NO toca
  // el leftstack (sidebar/sos/murphy permanecen abiertos para no perder
  // contexto operativo durante la comunicación radial).
  openRadioTetra({ commit, state, dispatch }, target) {
    if (state.panels.incidentDispatch) {
      commit('SET_PANEL', { panel: 'incidentDispatch', value: false })
      commit('SET_DISPATCH_TARGET', null)
    }
    if (state.panels.newIncident) {
      commit('SET_PANEL', { panel: 'newIncident', value: false })
      commit('SET_NEW_INCIDENT_SEED', null)
    }
    var t = target || null
    commit('SET_RADIO_TETRA_TARGET', t)
    var mode = (t && t.type === 'device') ? 'device' : 'channel'
    commit('SET_RADIO_TETRA_MODE', mode)
    commit('CLEAR_RADIO_PARTICIPANTS')
    commit('SET_PANEL', { panel: 'radioTetra', value: true })
    // Catálogo de groups (WS-upserted) carga vía radioGroups/load del
    // panel mismo cuando se monta. No hay nada que hidratar aquí.
  },

  closeRadioTetra({ commit }) {
    commit('SET_PANEL', { panel: 'radioTetra', value: false })
    commit('SET_RADIO_TETRA_TARGET', null)
    commit('CLEAR_RADIO_PARTICIPANTS')
    // Nota: no colgamos la radioCall activa al cerrar el panel — el
    // operador puede tener una llamada en curso y solo cerrar la UI.
    // El hangup se hace explícito con el botón Colgar del panel.
  },

  // Agrega un device TETRA adicional al canal de comunicación actual.
  // Invocado desde el diálogo "cambiar/agregar" cuando el operador
  // selecciona otro device TETRA con el panel ya abierto.
  addRadioParticipant({ commit }, device) {
    commit('ADD_RADIO_PARTICIPANT', device)
  },

  removeRadioParticipant({ commit }, uniqueId) {
    commit('REMOVE_RADIO_PARTICIPANT', uniqueId)
  },

  setRadioTetraTarget({ commit }, target) {
    commit('SET_RADIO_TETRA_TARGET', target || null)
    var mode = (target && target.type === 'device') ? 'device' : 'channel'
    commit('SET_RADIO_TETRA_MODE', mode)
  },

  setRadioTetraMode({ commit }, mode) {
    commit('SET_RADIO_TETRA_MODE', mode)
  },

  pushRadioActivity({ commit }, entry) {
    commit('PUSH_RADIO_ACTIVITY', entry)
    // Persistencia durable ligada al callId (fire-and-forget; no-op si
    // IndexedDB no está disponible). El chat en memoria se limpia al abrir
    // una nueva llamada, pero el historial completo queda en IndexedDB.
    radioTranscriptsDb.saveEntry(entry)
  },

  updateRadioActivity({ commit, state }, patch) {
    commit('UPDATE_RADIO_ACTIVITY', patch)
    // Re-persistir la entry parchada (texto final tardío, etc.).
    var updated = state.radioActivityLog.find(function(e) { return e.id === (patch && patch.id) })
    if (updated) radioTranscriptsDb.saveEntry(updated)
  },

  // Aplica el incidente fresco devuelto por el backend (cancel/close/
  // assign/change-status). Es no-op si el id no coincide con el
  // selectedItem actual.
  refreshSelectedIncident({ commit }, raw) {
    commit('REFRESH_SELECTED_ITEM_RAW', raw)
  },

  // Refresca el raw del item seleccionado (device, bus, lpr, sensor, etc.)
  // cuando llega un update por WS, para que el drawer abierto refleje el
  // estado real (status, última actualización, etc.) sin reseleccionar/F5.
  refreshSelectedItem({ commit }, raw) {
    commit('REFRESH_SELECTED_ITEM_RAW', raw)
  },

  startLocationPicker({ commit }, payload) {
    commit('START_LOCATION_PICKER', payload || {})
  },
  resolveLocationPicker({ commit }, result) {
    commit('RESOLVE_LOCATION_PICKER', result)
  },
  cancelLocationPicker({ commit }) {
    commit('CLEAR_LOCATION_PICKER')
  },

  selectItem({ commit, state }, payload) {
    commit('SET_SELECTED_ITEM', payload)
    // Open drawer without toggling (avoids the generic toggle path)
    if (!state.panels.drawer) {
      commit('SET_PANEL', { panel: 'drawer', value: true })
    }
  },

  clearSelection({ commit, state, dispatch }) {
    commit('SET_SELECTED_ITEM', null)
    if (state.panels.drawer) {
      commit('SET_PANEL', { panel: 'drawer', value: false })
    }
  },

  pinCurrent({ commit }) {
    commit('PIN_CURRENT')
  },

  unpinItem({ commit }, monitorId) {
    commit('UNPIN_ITEM', monitorId)
  },

  // payload = { monitorId, data, entityType }
  // Llamado desde MapV2.vue cuando el usuario clickea una pestaña.
  // Quien invoca debe haber resuelto ya 'data' desde this._monitors
  // (fresca) o del pin persistido (stale).
  selectPinned({ dispatch }, payload) {
    dispatch('selectItem', payload)
  },

  clearAllPins({ commit }) {
    commit('CLEAR_ALL_PINS')
  },

  pinCamera({ commit, state }, camera) {
    commit('PIN_CAMERA', camera)
    if (!state.panels.tray) {
      commit('SET_PANEL', { panel: 'tray', value: true })
    }
  },

  // Resuelve el userId desde el store de auth y garantiza que _userId/persistencia
  // estén listos. Idempotente. Se llama antes de cualquier operación de presets
  // y al montar el mapa, para no depender de que SET_LOGIN haya corrido.
  ensureTrayPresetsUser({ commit, state, rootState }) {
    const u = rootState.auth && rootState.auth.user
    const uid = u && (u.id != null ? u.id : (u._id != null ? u._id : null))
    if (uid == null || state._userId === uid) return
    commit('ENSURE_PRESETS_USER', uid)
  },

  // Sincroniza los presets del usuario con apiv2 y, la primera vez en este
  // origen, sube los que había en localStorage (migración por cambio de
  // dominio). Idempotente mientras está en vuelo.
  syncTrayPresets({ commit, state, dispatch }) {
    dispatch('ensureTrayPresetsUser')
    const uid = state._userId
    if (uid == null) return Promise.resolve()
    if (presetSync && presetSync.uid === uid) return presetSync.promise
    const current = { uid, pendingOps: [], promise: null }
    current.promise = syncPresets({
      api: TrayPresetsService,
      localPresets: state.trayPresets,
      migrated: ls('trayPresetsMigrated:' + uid, false),
    })
      .then((res) => {
        if (res.error) console.error('[tray-presets] sync', { err: res.error })
        if (state._userId !== uid) return
        // El operador cree que con entrar ya respaldó sus presets: si el
        // import falló tiene que saberlo (se reintenta en el próximo sync).
        if (!res.ok && !res.migrated && res.presets.length && window.VMA) {
          window.VMA.showError({
            title: 'No se pudieron respaldar tus presets del video wall. Inténtalo de nuevo o avisa a soporte.',
          })
        }
        if (res.migrated) lsSet('trayPresetsMigrated:' + uid, true)
        if (res.ok) commit('SET_TRAY_PRESETS', applyPendingOps(res.presets, current.pendingOps))
      })
      .catch((err) => { console.error('[tray-presets] sync', { err }) })
      .finally(() => { if (presetSync === current) presetSync = null })
    presetSync = current
    return current.promise
  },

  saveCurrentAsTrayPreset({ commit, state, dispatch }, name) {
    dispatch('ensureTrayPresetsUser')
    const camById = {}
    state.tray.cameras.forEach(function(c) { camById[c.id] = c })
    const preset = {
      id: 'preset-' + Date.now() + '-' + Math.floor(Math.random() * 1e6),
      name: (name || 'Preset').trim(),
      createdAt: Date.now(),
      cols: state.tray.grid.cols,
      tiles: buildPresetTiles(state.tray.grid.tiles, camById),
    }
    commit('SAVE_TRAY_PRESET', preset)
    trackPendingPresetOp({ type: 'upsert', preset })
    pushPresetWrite(state, 'save', () => TrayPresetsService.save(preset))
    return preset
  },

  deleteTrayPreset({ commit, state, dispatch }, id) {
    dispatch('ensureTrayPresetsUser')
    commit('DELETE_TRAY_PRESET', id)
    trackPendingPresetOp({ type: 'delete', id })
    pushPresetWrite(state, 'delete', () => TrayPresetsService.remove(id))
  },

  // Sobrescribe el preset activo con la configuración actual del tray (mismo
  // id y nombre). Upsert vía SAVE_TRAY_PRESET → limpia el flag "modificado".
  updateActiveTrayPreset({ commit, state, dispatch }) {
    dispatch('ensureTrayPresetsUser')
    const active = state.trayPresets.find(function(p) { return p.id === state.activePresetId })
    if (!active) return
    const camById = {}
    state.tray.cameras.forEach(function(c) { camById[c.id] = c })
    const preset = {
      ...active,
      cols: state.tray.grid.cols,
      tiles: buildPresetTiles(state.tray.grid.tiles, camById),
    }
    commit('SAVE_TRAY_PRESET', preset)
    trackPendingPresetOp({ type: 'upsert', preset })
    pushPresetWrite(state, 'update', () => TrayPresetsService.save(preset))
    return preset
  },

  // resolver: función (cameraRef) => cameraObjLive | null, provista por MapV2.vue
  applyTrayPreset({ commit, state, dispatch }, { presetId, resolver }) {
    dispatch('ensureTrayPresetsUser')
    const preset = state.trayPresets.find(function(p) { return p.id === presetId })
    if (!preset) return
    const resolved = resolvePresetToTray(preset, resolver)
    commit('APPLY_TRAY_PRESET', {
      presetId: presetId,
      cameras: resolved.cameras,
      grid: resolved.grid,
    })
    if (!state.panels.tray) commit('SET_PANEL', { panel: 'tray', value: true })
  },

  enterTrayFullscreen({ commit }) {
    commit('SET_TRAY_MODE', 'fullscreen')
  },

  exitTrayFullscreen({ commit, state }) {
    // Volver al modo previo. Guard contra previousMode invalido:
    // si quedo en un modo especial o vacio (ej. migracion desde version
    // anterior del store), fallback a docked.
    const target = state.tray.previousMode
    const SPECIAL = ['popout', 'fullscreen']
    const safe = (!target || SPECIAL.includes(target)) ? 'docked' : target
    commit('SET_TRAY_MODE', safe)
  },

  openMurphyResult({ commit, state }, normalizedResult) {
    if (!normalizedResult) return
    // Si el id ya esta pineado, heredamos el view state (paginador de
    // homonimos + filtros) persistido para no perder lo que el operador
    // tenia configurado. La data/lprData/personas del pin se ignoran en
    // este path: una busqueda fresca siempre usa el resultado nuevo.
    var pin = state.pinnedMurphyItems.find(function(p) { return p.id === normalizedResult.id })
    var merged = normalizedResult
    if (pin) {
      merged = {
        ...normalizedResult,
        personaIdx: pin.personaIdx || 0,
        filterEstado: pin.filterEstado || null,
        filterFecha: pin.filterFecha || '',
        filtersVisible: !!pin.filtersVisible,
      }
    }
    commit('SET_MURPHY_RESULT', merged)
    if (!state.panels.murphyDrawer) {
      commit('SET_PANEL', { panel: 'murphyDrawer', value: true })
    }
    if (pin) {
      // Activo por default si ya estaba pineado; si no, se marca al pinear
      commit('SET_ACTIVE_MURPHY_PIN', merged.id)
      // Sincronizar snapshot de data + title en el pin para que reloads
      // muestren la informacion mas fresca sin re-consultar Murphy.
      var patch = {
        data: merged.data || null,
        lprData: merged.lprData || [],
        personas: merged.personas || [],
        fetchedAt: merged.fetchedAt,
      }
      if (pin.title !== merged.title) patch.title = merged.title
      commit('UPDATE_PINNED_MURPHY', { id: merged.id, patch: patch })
    } else {
      commit('SET_ACTIVE_MURPHY_PIN', null)
    }
  },

  selectMurphyPin({ commit, state }, id) {
    if (state.murphyResult && state.murphyResult.id === id) return
    var pin = state.pinnedMurphyItems.find(function(p) { return p.id === id })
    if (!pin) return
    // Hidratar murphyResult con la data completa del pin — status 'fresh'
    // porque los datos vienen del snapshot persistido al momento de pinear
    // (o del ultimo refetch). El operador puede usar el boton Actualizar
    // si considera que la info esta desactualizada.
    commit('SET_MURPHY_RESULT', {
      id: pin.id,
      kind: pin.kind,
      title: pin.title,
      queryName: pin.queryName,
      queryParams: pin.queryParams,
      data: pin.data || null,
      lprData: pin.lprData || [],
      personas: pin.personas || [],
      fetchedAt: pin.fetchedAt || pin.pinnedAt,
      status: 'fresh',
      errorMessage: null,
      personaIdx: pin.personaIdx || 0,
      filterEstado: pin.filterEstado || null,
      filterFecha: pin.filterFecha || '',
      filtersVisible: !!pin.filtersVisible,
    })
    commit('SET_ACTIVE_MURPHY_PIN', id)
    if (!state.panels.murphyDrawer) {
      commit('SET_PANEL', { panel: 'murphyDrawer', value: true })
    }
  },

  // Sincroniza el view state del operador (paginador de homonimos + filtros)
  // al murphyResult actual y, si esta pineado, al pin persistido. Lo llama
  // MurphyDrawer cada vez que el usuario cambia murphyPersonaIdx, el filtro
  // de estado, el filtro de fecha o el toggle de filtros visibles.
  updateMurphyViewState({ commit, state }, patch) {
    if (!state.murphyResult) return
    var next = { ...state.murphyResult }
    var pinPatch = {}
    if (patch.personaIdx !== undefined) {
      next.personaIdx = patch.personaIdx
      pinPatch.personaIdx = patch.personaIdx
    }
    if (patch.filterEstado !== undefined) {
      next.filterEstado = patch.filterEstado
      pinPatch.filterEstado = patch.filterEstado
    }
    if (patch.filterFecha !== undefined) {
      next.filterFecha = patch.filterFecha
      pinPatch.filterFecha = patch.filterFecha
    }
    if (patch.filtersVisible !== undefined) {
      next.filtersVisible = patch.filtersVisible
      pinPatch.filtersVisible = patch.filtersVisible
    }
    commit('SET_MURPHY_RESULT', next)
    if (state.activeMurphyPinId === next.id) {
      commit('UPDATE_PINNED_MURPHY', { id: next.id, patch: pinPatch })
    }
  },

  async refetchMurphyResult({ commit, state, rootGetters }) {
    var current = state.murphyResult
    if (!current || !current.queryName || !current.queryParams) return

    var user = rootGetters['auth/getUser']
    var murphyUser = user && user.attributes ? user.attributes.consulta_murphy : null
    if (!murphyUser) {
      commit('SET_MURPHY_STATUS', {
        status: 'error',
        errorMessage: 'No tienes permisos para consultar Murphy',
      })
      return
    }

    commit('SET_MURPHY_STATUS', { status: 'loading', errorMessage: null })

    try {
      var murphyService = new MurphyService()
      var result = await murphyService.createQuery(current.queryName, murphyUser, current.queryParams)
      if (!result || !result.queryObjects || result.queryObjects.length === 0) {
        commit('SET_MURPHY_STATUS', {
          status: 'error',
          errorMessage: 'Sin resultados en Murphy para los parametros guardados',
        })
        return
      }

      // Reconstruir el shape normalizado como si hubiera venido del MurphyPanel
      var rebuilt
      if (current.kind === 'vehiculo') {
        var extracted = extractVehiculoFromMurphyResult(result.queryObjects)
        var lprData = extractLprDataFromMurphyResult(result.queryObjects)
        rebuilt = {
          ...current,
          data: extracted.vehiculoData,
          roboData: extracted.roboData,
          placasAsociadas: extracted.placasAsociadas,
          lprData: lprData,
          personas: [],
          fetchedAt: Date.now(),
          status: 'fresh',
          errorMessage: null,
        }
      } else {
        var personas = []
        result.queryObjects.forEach(function(obj) { personas.push(obj.properties) })
        rebuilt = {
          ...current,
          data: null,
          lprData: [],
          personas: personas,
          fetchedAt: Date.now(),
          status: 'fresh',
          errorMessage: null,
        }
      }

      commit('SET_MURPHY_RESULT', rebuilt)
      // Si el result esta pineado, actualizar el snapshot del pin con la
      // data fresca para que el reload siguiente muestre esto sin refetch.
      if (state.activeMurphyPinId === rebuilt.id) {
        commit('UPDATE_PINNED_MURPHY', {
          id: rebuilt.id,
          patch: {
            data: rebuilt.data,
            roboData: rebuilt.roboData,
            placasAsociadas: rebuilt.placasAsociadas,
            lprData: rebuilt.lprData,
            personas: rebuilt.personas,
            fetchedAt: rebuilt.fetchedAt,
          },
        })
      }
    } catch (err) {
      console.error('[Murphy refetch]', err)
      commit('SET_MURPHY_STATUS', {
        status: 'error',
        errorMessage: 'Error al consultar Murphy',
      })
    }
  },

  // Accion rapida: buscar una placa en Murphy desde cualquier parte de la
  // app (ej. boton al lado de la placa en un hit LPR). Replica el flujo
  // de MurphyPanel._buscar pero parametrizado con la placa y sin UI
  // propia. Si hay resultado, abre el panel Murphy con la data lista.
  async searchMurphyByPlaca({ dispatch, rootGetters }, placa) {
    if (!placa) return
    var user = rootGetters['auth/getUser']
    var murphyUser = user && user.attributes ? user.attributes.consulta_murphy : null
    if (!murphyUser) {
      if (window.VMA && window.VMA.showError) {
        window.VMA.showError({ title: 'No tienes permisos para consultar Murphy' })
      }
      return
    }
    if (window.VMA && window.VMA.loading) window.VMA.loading(true)
    try {
      var svc = new MurphyService()
      var result = await svc.createQuery('VehiculosPlaca', murphyUser, { placa: placa })
      if (!result || !result.queryObjects || result.queryObjects.length === 0) {
        if (window.VMA && window.VMA.showError) {
          window.VMA.showError({ title: 'Sin resultados en Murphy para ' + placa })
        }
        return
      }
      // Extracción consolidada por fuente (ver murphy-vehiculo-extractor.js)
      // — preserva la semántica de fecha_robo / domicilio / averiguacion /
      // reporte que el Object.assign-ish previo aplastaba.
      var extracted = extractVehiculoFromMurphyResult(result.queryObjects)
      var lprData = extractLprDataFromMurphyResult(result.queryObjects)
      var normalized = normalizeMurphyResult({
        type: 'vehiculo',
        data: extracted.vehiculoData,
        roboData: extracted.roboData,
        placasAsociadas: extracted.placasAsociadas,
        lprData: lprData,
      })
      dispatch('openMurphyResult', normalized)
    } catch (err) {
      console.error('[Murphy quick search]', err)
      if (window.VMA && window.VMA.showError) {
        window.VMA.showError({ title: 'Error al consultar Murphy' })
      }
    } finally {
      if (window.VMA && window.VMA.loading) window.VMA.loading(false)
    }
  },

  pinCurrentMurphy({ commit }) {
    commit('PIN_CURRENT_MURPHY')
  },

  unpinMurphy({ commit }, id) {
    commit('UNPIN_MURPHY', id)
  },

  clearAllMurphyPins({ commit }) {
    commit('CLEAR_ALL_MURPHY_PINS')
  },

  clearFaceSearchState({ commit }) {
    commit('RESET_FACE_SEARCH_FILTERS')
  },

  // Borra la caché IndexedDB de entidades. La caché ya es por usuario y no se
  // cruza (entity-cache.js); esto es para no dejar en disco, tras el logout,
  // el padrón completo que el backend le entregó al operador.
  clearEntityCache() {
    return clearEntityCacheDb()
  },
}

const getters = {
  isLayerVisible: (state) => (entityType) => {
    return state.layerVisibility[entityType] !== false
  },

  // Agregado derivado para codigo existente: true si cualquier sub-filtro
  // esta activo. El botón del toolbar y otras referencias siguen usando
  // este flag para su estado "activo/inactivo" global.
  geofencesVisible: (state) => {
    var f = state.geofenceFilters || {}
    return !!(f.geofence || f.region || f.sector || f.cuadrante)
  },

  visibleEntityTypes: (state) => {
    return Object.entries(state.layerVisibility)
      .filter(([, visible]) => visible)
      .map(([type]) => type)
  },

  pinnedCameraCount: (state) => {
    return state.tray.cameras.length
  },

  trayPresets: (state) => state.trayPresets,
  activePresetId: (state) => state.activePresetId,
  activePreset: (state) => state.trayPresets.find(p => p.id === state.activePresetId) || null,
  // true cuando el tray divergió del preset activo sin guardar (habilita
  // "Guardar cambios en «X»").
  activePresetDirty: (state) => !!state.activePresetId && state.activePresetDirty,
  // true cuando hay cámaras pero NO hay preset activo (configuración ad-hoc,
  // p.ej. tras un reload sin preset). Habilita "Guardar como preset nuevo".
  isTrayUnsaved: (state) => state.tray.cameras.length > 0 && !state.activePresetId,

  pinnedCount: (state) => state.pinnedItems.length,

  isPinned: (state) => (monitorId) => {
    if (!monitorId) return false
    return state.pinnedItems.some(function(p) { return p.monitorId === monitorId })
  },

  murphyPinnedCount: (state) => state.pinnedMurphyItems.length,

  isMurphyPinned: (state) => (id) => {
    if (!id) return false
    return state.pinnedMurphyItems.some(function(p) { return p.id === id })
  },

  hasMurphyResult: (state) => !!state.murphyResult,

  faceSearchFilters: (state) => state.faceSearchFilters,
  smartSearchInitialMode: (state) => state.smartSearchInitialMode,
  newIncidentSeed: (state) => state.newIncidentSeed,
  dispatchTargetIncidentId: (state) => state.dispatchTargetIncidentId,
  locationPicker: (state) => state.locationPicker,

  // ───────── Radio TETRA getters ─────────
  radioTetraTarget: (state) => state.radioTetraTarget,
  radioTetraMode: (state) => state.radioTetraMode,
  // radioTalking se deriva en el panel a partir de radioCall.active.floor —
  // ya no vive como state propio en este módulo.
  radioTetraParticipants: (state) => state.radioTetraParticipants,
  // radioActiveChannelIds + radioChannels se mueven al panel via radioCall
  // (peer activo) y radioGroups (catálogo), respectivamente.
  radioActivityLog: (state) => state.radioActivityLog,
  followedUnit: (state) => state.followedUnit,
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
